import { useUser } from 'contexts/UserContext'
import { menuItems } from 'data/menuItems'
import useClickOutside from 'hooks/useClickOutside'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuChevronDown, LuLogOut, LuMenu } from 'react-icons/lu'
import { useLocation } from 'wouter'
import LanguageSelector from 'components/ui/LanguageSelector'
import ProjectsDropdown from './ProjectsDropdown'
import CompaniesDropdown from './CompaniesDropdown'
import { isAdminRole } from 'utils/roles'

// Título de la página actual a partir del menú (la ruta más específica gana)
function usePageTitle() {
  const { t } = useTranslation()
  const [location] = useLocation()
  const match = menuItems
    .filter(item => (item.slug === '/' ? location === '/' : location === item.slug || location.startsWith(`${item.slug}/`)))
    .sort((a, b) => b.slug.length - a.slug.length)[0]
  return match ? t(match.title) : ''
}

export default function Navbar({ onOpenMobileMenu }) {
  const { t } = useTranslation('layout')
  const { user } = useUser()
  const [, setLocation] = useLocation()
  const title = usePageTitle()

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useClickOutside(() => setUserMenuOpen(false))

  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || ''
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
  const isAdmin = isAdminRole(user?.role)

  return (
    <header className="flex h-16 flex-none items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 md:px-8">
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          onClick={onOpenMobileMenu}
          aria-label={t('navbar.open_menu')}
          className="-ml-2 flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50 md:hidden"
        >
          <LuMenu size={20} aria-hidden="true" />
        </button>
        <span className="truncate text-sm font-medium text-gray-900">{title}</span>
      </div>

      <div className="flex items-center gap-2">
        {/* Solo admins: elegir qué empresa y qué proyecto ver */}
        {isAdmin && (
          <div className="hidden items-center gap-2 lg:flex">
            <CompaniesDropdown />
            <ProjectsDropdown />
          </div>
        )}

        <LanguageSelector variant="light" />

        <div ref={userMenuRef} className="relative">
          <button
            type="button"
            onClick={() => setUserMenuOpen(prev => !prev)}
            aria-label={t('navbar.user_menu')}
            aria-expanded={userMenuOpen}
            className="flex h-9 items-center gap-1.5 rounded-lg pl-1 pr-1.5 text-gray-600 hover:bg-gray-50"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand">
              {initials || '·'}
            </span>
            <LuChevronDown size={16} aria-hidden="true" />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-11 z-50 w-64 rounded-xl border border-gray-200 bg-white py-2 shadow-lg">
              <div className="border-b border-gray-100 px-4 pb-3 pt-1">
                <p className="truncate text-sm font-semibold text-gray-900">{name}</p>
                <p className="truncate text-xs text-gray-500">{user?.email}</p>
              </div>

              {/* En pantallas medianas los selectores de admin viven aquí */}
              {isAdmin && (
                <div className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 lg:hidden">
                  <CompaniesDropdown />
                  <ProjectsDropdown />
                </div>
              )}

              <button
                type="button"
                onClick={() => setLocation('/logout')}
                className="mt-1 flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <LuLogOut size={16} aria-hidden="true" />
                {t('navbar.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

Navbar.propTypes = {
  onOpenMobileMenu: PropTypes.func.isRequired,
}
